/**
 * Syncs refreshed JWT access tokens to specific embedded iframes via window.postMessage.
 *
 * The parent app refreshes tokens every 4 minutes, but iframes on different
 * origins (e.g. prototypes.merciyanis.dev) cannot access the parent's
 * localStorage. This module bridges that gap by pushing the new token
 * to targeted iframes after every successful refresh.
 *
 * The targetOrigin parameter in postMessage ensures the browser only delivers
 * the message if the iframe's actual origin matches — no token leaks to
 * unexpected origins.
 */

/**
 * List of iframes that should receive token updates.
 * Each entry maps an iframe id to its expected origin.
 * Add new entries here when onboarding additional iframe apps.
 */
const TOKEN_SYNC_TARGETS = [
    { id: 'beta-kanban-iframe', origin: 'https://prototypes.merciyanis.dev' },
];

/**
 * Sends the new access token to all targeted iframes via postMessage.
 * Called from the auth reducer on every successful login / token refresh
 * (JWT, Cryptr SSO, and auto-refresh all share the same code path).
 *
 * Uses { type: 'token', data: '<jwt>' } format to match the existing
 * postMessage listener in the iframe apps.
 *
 * @param {string} accessToken - The fresh JWT access token
 */
export const broadcastTokenToIframes = (accessToken) => {
    if (accessToken === undefined || accessToken === null || accessToken === '') return;

    TOKEN_SYNC_TARGETS.forEach(({ id, origin }) => {
        // getElementById is a direct hash lookup — faster than querySelector
        const iframe = document.getElementById(id);
        // iframe not on the page (user is on a different view)
        if (iframe === null) return;
        // contentWindow is null when the iframe has been detached from the DOM
        if (iframe.contentWindow === null) return;

        try {
            iframe.contentWindow.postMessage({ type: 'token', data: accessToken }, origin);
        } catch (error) {
            // Race condition: iframe removed between getElementById and postMessage
            console.warn(`[iframeTokenSync] postMessage failed for "${id}":`, error.message);
        }
    });
};
